'use client';
import React, { useEffect, useRef } from 'react';

const testimonials = [
  {
    name: 'Marcus T.',
    location: 'Moncton, NB',
    rating: 5,
    photo: 'https://i.pravatar.cc/150?img=11',
    photoAlt: 'Marcus T., satisfied ATL Detailing customer from Moncton',
    review:
      'Booked the Executive package and honestly could not believe the results. My seats looked brand new after the steam clean. The team was professional, on time, and the car smelled incredible.',
    service: 'Executive Detailing',
    highlight: 'Steam Clean All Fabric',
  },
  {
    name: 'Priya S.',
    location: 'Dieppe, NB',
    rating: 5,
    photo: 'https://i.pravatar.cc/150?img=47',
    photoAlt: 'Priya S., happy ATL Detailing customer from Dieppe',
    review:
      'I had serious pet hair all over my back seat. They got every single strand out. I have tried other detailers before but ATL is on a completely different level. Will not go anywhere else.',
    service: 'Premium Detailing + Pet Hair Removal',
    highlight: 'Pet Hair Removal',
  },
  {
    name: 'Jordan L.',
    location: 'Riverview, NB',
    rating: 5,
    photo: 'https://i.pravatar.cc/150?img=33',
    photoAlt: 'Jordan L., loyal ATL Detailing customer from Riverview',
    review:
      'Salt stains from winter were destroying my interior. ATL removed them completely and applied fabric protection so it does not happen again. Booking again before next winter for sure.',
    service: 'Executive Detailing + Salt Stain Removal',
    highlight: 'Salt Stains Removal',
  },
  {
    name: 'Danielle R.',
    location: 'Moncton, NB',
    rating: 5,
    photo: 'https://i.pravatar.cc/150?img=25',
    photoAlt: 'Danielle R., repeat ATL Detailing customer from Moncton',
    review:
      'My car had a stubborn coffee stain on the passenger seat for months. One visit and it was completely gone. The dashboard and console looked showroom fresh. Absolutely worth every dollar.',
    service: 'Premium Detailing + Stain Removal',
    highlight: 'Stubborn Stain Removal',
  },
  {
    name: 'Kevin O.',
    location: 'Moncton, NB',
    rating: 5,
    photo: 'https://i.pravatar.cc/150?img=52',
    photoAlt: 'Kevin O., satisfied ATL Detailing customer from Moncton',
    review:
      'Had the house wash done on my driveway and gutters. The difference was night and day. Fast, thorough, and they cleaned up everything after. Highly recommend ATL for any detailing or property work.',
    service: 'Property Wash',
    highlight: 'Driveway & Gutter Cleaning',
  },
];

function StarRating({ count }: { count: number }) {
  return (
    <div className="flex gap-0.5">
      {Array.from({ length: count }).map((_, i) => (
        <svg
          key={i}
          width="16"
          height="16"
          viewBox="0 0 20 20"
          fill="currentColor"
          className="text-yellow-400"
        >
          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
        </svg>
      ))}
    </div>
  );
}

export default function TestimonialsSection() {
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            el.querySelectorAll('.reveal:not(.visible)').forEach((node, i) => {
              setTimeout(() => node.classList.add('visible'), i * 120);
            });
          }
        });
      },
      { threshold: 0.06 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      style={{ background: 'var(--cockpit-black)' }}
      className="relative py-24 px-4 overflow-hidden"
    >
      {/* Ambient glow */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            'radial-gradient(ellipse 70% 40% at 50% 0%, rgba(59,130,246,0.07) 0%, transparent 70%)',
        }}
      />

      <div className="max-w-6xl mx-auto relative z-10">
        {/* Header */}
        <div className="text-center mb-16 reveal">
          <span className="metric-pill mb-5 inline-flex">
            <svg width="12" height="12" viewBox="0 0 20 20" fill="currentColor" className="text-yellow-400">
              <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
            </svg>
            5-Star Reviews
          </span>
          <h2
            className="text-4xl md:text-5xl font-extrabold mb-4"
            style={{ color: 'var(--white)', fontFamily: 'Manrope, sans-serif', letterSpacing: '-0.02em' }}
          >
            What Our Customers Say
          </h2>
          <p
            className="text-lg max-w-xl mx-auto"
            style={{ color: 'var(--aluminum-dim)' }}
          >
            Real results from real Moncton drivers. Every review is from a verified customer.
          </p>
        </div>

        {/* Testimonials grid — asymmetric bento layout */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
          {/* Large featured card */}
          <div
            className="reveal md:col-span-7 rounded-2xl p-7 flex flex-col justify-between"
            style={{
              background: 'linear-gradient(135deg, rgba(59,130,246,0.12) 0%, rgba(27,27,27,0.9) 60%)',
              border: '1px solid rgba(59,130,246,0.25)',
            }}
          >
            <div>
              <div className="flex items-start gap-4 mb-5">
                <div className="relative flex-shrink-0">
                  <img
                    src={testimonials[0].photo}
                    alt={testimonials[0].photoAlt}
                    className="w-14 h-14 rounded-full object-cover"
                    style={{ border: '2px solid rgba(59,130,246,0.4)' }}
                  />
                  <div
                    className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full flex items-center justify-center"
                    style={{ background: 'var(--ceramic-blue)' }}
                  >
                    <svg width="10" height="10" viewBox="0 0 20 20" fill="white">
                      <path d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" />
                    </svg>
                  </div>
                </div>
                <div>
                  <p className="font-bold text-white" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    {testimonials[0].name}
                  </p>
                  <p className="text-xs" style={{ color: 'var(--aluminum-dim)' }}>
                    {testimonials[0].location}
                  </p>
                  <StarRating count={testimonials[0].rating} />
                </div>
              </div>
              <svg
                className="mb-3 opacity-30"
                width="28"
                height="20"
                viewBox="0 0 28 20"
                fill="none"
              >
                <path
                  d="M0 20V12.667C0 5.556 4.222 1.333 12.667 0l1.333 2C9.778 3.111 7.556 5.333 7.111 8.889H12V20H0zm16 0V12.667C16 5.556 20.222 1.333 28.667 0L30 2c-4.222 1.111-6.444 3.333-6.889 6.889H28V20H16z"
                  fill="var(--ceramic-blue)"
                />
              </svg>
              <p
                className="text-base leading-relaxed mb-5"
                style={{ color: 'var(--polished-aluminum)' }}
              >
                {testimonials[0].review}
              </p>
            </div>
            <div className="flex items-center justify-between">
              <span
                className="text-xs font-semibold px-3 py-1.5 rounded-full"
                style={{
                  background: 'rgba(59,130,246,0.15)',
                  color: 'var(--ceramic-blue)',
                  fontFamily: 'Manrope, sans-serif',
                  border: '1px solid rgba(59,130,246,0.2)',
                }}
              >
                {testimonials[0].service}
              </span>
              <div className="flex gap-0.5">
                {[1, 2, 3, 4, 5].map((s) => (
                  <svg key={s} width="14" height="14" viewBox="0 0 20 20" fill="#FACC15">
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                  </svg>
                ))}
              </div>
            </div>
          </div>

          {/* Right column — 2 stacked cards */}
          <div className="md:col-span-5 flex flex-col gap-4">
            {[testimonials[1], testimonials[2]].map((t, idx) => (
              <div
                key={t.name}
                className={`reveal rounded-2xl p-6 flex flex-col justify-between ${idx === 0 ? 'reveal-delay-1' : 'reveal-delay-2'}`}
                style={{
                  background: 'rgba(27,27,27,0.85)',
                  border: '1px solid rgba(255,255,255,0.07)',
                }}
              >
                <div className="flex items-center gap-3 mb-4">
                  <img
                    src={t.photo}
                    alt={t.photoAlt}
                    className="w-11 h-11 rounded-full object-cover flex-shrink-0"
                    style={{ border: '1.5px solid rgba(255,255,255,0.12)' }}
                  />
                  <div>
                    <p className="font-bold text-sm text-white" style={{ fontFamily: 'Manrope, sans-serif' }}>
                      {t.name}
                    </p>
                    <p className="text-xs" style={{ color: 'var(--aluminum-dim)' }}>
                      {t.location}
                    </p>
                    <StarRating count={t.rating} />
                  </div>
                </div>
                <p className="text-sm leading-relaxed mb-4" style={{ color: 'var(--polished-aluminum)' }}>
                  {t.review}
                </p>
                <span
                  className="text-xs font-semibold self-start px-3 py-1 rounded-full"
                  style={{
                    background: 'rgba(255,255,255,0.05)',
                    color: 'var(--aluminum-dim)',
                    fontFamily: 'Manrope, sans-serif',
                    border: '1px solid rgba(255,255,255,0.08)',
                  }}
                >
                  {t.service}
                </span>
              </div>
            ))}
          </div>

          {/* Bottom row — 2 equal cards */}
          {[testimonials[3], testimonials[4]].map((t, idx) => (
            <div
              key={t.name}
              className={`reveal md:col-span-6 rounded-2xl p-6 ${idx === 0 ? 'reveal-delay-2' : 'reveal-delay-3'}`}
              style={{
                background: 'rgba(27,27,27,0.85)',
                border: '1px solid rgba(255,255,255,0.07)',
              }}
            >
              <div className="flex items-center gap-3 mb-4">
                <img
                  src={t.photo}
                  alt={t.photoAlt}
                  className="w-11 h-11 rounded-full object-cover flex-shrink-0"
                  style={{ border: '1.5px solid rgba(255,255,255,0.12)' }}
                />
                <div>
                  <p className="font-bold text-sm text-white" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    {t.name}
                  </p>
                  <p className="text-xs" style={{ color: 'var(--aluminum-dim)' }}>
                    {t.location}
                  </p>
                  <StarRating count={t.rating} />
                </div>
              </div>
              <p className="text-sm leading-relaxed mb-4" style={{ color: 'var(--polished-aluminum)' }}>
                {t.review}
              </p>
              <span
                className="text-xs font-semibold self-start px-3 py-1 rounded-full inline-block"
                style={{
                  background: 'rgba(255,255,255,0.05)',
                  color: 'var(--aluminum-dim)',
                  fontFamily: 'Manrope, sans-serif',
                  border: '1px solid rgba(255,255,255,0.08)',
                }}
              >
                {t.service}
              </span>
            </div>
          ))}
        </div>

        {/* Trust bar */}
        <div
          className="reveal reveal-delay-4 mt-12 rounded-2xl px-8 py-6 flex flex-wrap items-center justify-center gap-8"
          style={{
            background: 'rgba(255,255,255,0.03)',
            border: '1px solid rgba(255,255,255,0.06)',
          }}
        >
          {[
            { value: '5.0', label: 'Average Rating' },
            { value: '100%', label: 'Satisfaction Rate' },
            { value: 'Moncton', label: 'Locally Trusted' },
            { value: 'Verified', label: 'Real Customers' },
          ].map((stat) => (
            <div key={stat.label} className="text-center">
              <p
                className="text-2xl font-extrabold"
                style={{ color: 'var(--white)', fontFamily: 'Manrope, sans-serif' }}
              >
                {stat.value}
              </p>
              <p className="text-xs mt-0.5" style={{ color: 'var(--aluminum-dim)' }}>
                {stat.label}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
